import React, { useState, useRef } from "react";
import { base44 } from "@/race-tracker/api/rtClient";
import { supabase } from "@/lib/supabase";
import { Button } from "@/race-tracker/components/ui/button";
import { Card } from "@/race-tracker/components/ui/card";
import { Badge } from "@/race-tracker/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/race-tracker/components/ui/table";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Download, Loader2 } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { normalizeChamber, RT_CHAMBER_VALUES } from "@/race-tracker/lib/sweepUtils";
import { format } from "date-fns";

const ANALYSTS = ["Dan", "Evan", "Ian", "James"];
const TEAM_MEMBERS = ["Lauren", "Paul", "Kyle", "Rachel", "Kiera", "Casey", "Rande", "Max"];
const AGENCIES = ["SMG", "DCM", "DRM", "SMG/DCM", "No Client"];

const IMPORT_CHUNK_SIZE = 500;

/** First-row wins for duplicate advertiser_name (matches sequential import). */
function buildAdvertiserPayload(row) {
  return {
    advertiser_name: row.advertiser_name,
    candidate_name: row.candidate_name || "",
    agency: AGENCIES.includes(row.agency) ? row.agency : "SMG",
    account_executive: row.ae || "",
    account_manager: row.am || "",
    data_analyst: row.da,
    smg_plus: row.smg_plus?.toLowerCase() === "yes" || row.smg_plus?.toLowerCase() === "true",
    status: ["Active", "Inactive", "Done"].includes(row.status) ? row.status : "Active",
  };
}

function raceDedupKeyFromRow(row) {
  const chamber = normalizeChamber(row.office || row.chamber || "");
  const district = row.race || row.district || "";
  return `${row.advertiser_name}|${row.state}|${chamber}|${district}`;
}

function buildRacePayload(row, advertiserId) {
  const chamber = normalizeChamber(row.office || row.chamber || "");
  return {
    advertiser_id: advertiserId,
    state: row.state,
    chamber,
    district: row.race || row.district || "",
    election_type: row.election_type || "",
    election_date: row.primary_date || row.election_date || "",
    runoff_date: row.runoff_date || "",
    markets: row.markets || "",
    race_importance: row.race_imp_rating ? Number(row.race_imp_rating) : null,
    ge_rating: row.ge_rating || "",
    reference_link: row.link || row.reference_link || "",
    last_digital_sweep: row.last_digital_sweep || "",
    last_linear_sweep: row.last_linear_sweep || "",
    status: ["Active", "Inactive", "Done"].includes(row.status) ? row.status : "Active",
    notes: row.notes || "",
    notes_updated_at: row.notes ? new Date().toISOString() : "",
  };
}

/** Map List View CSV export headers onto short import field names (only when target is empty). */
function mergeListExportColumns(row) {
  const empty = (v) => v === undefined || v === null || String(v).trim() === "";
  if (empty(row.da) && row.data_analyst) row.da = String(row.data_analyst).trim();
  if (empty(row.ae) && row.account_executive) row.ae = String(row.account_executive).trim();
  if (empty(row.am) && row.account_manager) row.am = String(row.account_manager).trim();
  if (empty(row.ge_rating) && row.general_election_rating) row.ge_rating = String(row.general_election_rating).trim();
  if (empty(row.race_imp_rating) && !empty(row.race_importance_rating)) {
    row.race_imp_rating = String(row.race_importance_rating).trim();
  }
  if (empty(row.opponent_name) && row.opponents) row.opponent_name = String(row.opponents).trim();
  if (empty(row.ballotpedia_url) && row.ballotpedia_links) row.ballotpedia_url = String(row.ballotpedia_links).trim();
  if (empty(row.notes) && row.race_notes) row.notes = String(row.race_notes).trim();
  if (empty(row.smg_plus) && !empty(row["smg+"])) row.smg_plus = String(row["smg+"]).trim();
  if (empty(row.link) && row.reference_link) row.link = String(row.reference_link).trim();
}

export default function ImportPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [importFatalError, setImportFatalError] = useState(null);
  const [importFileName, setImportFileName] = useState("");

  const { data: importLogs = [], isLoading: importLogsLoading } = useQuery({
    queryKey: ["rtCsvImportLogs"],
    queryFn: () => base44.entities.CsvImportLog.listRecent(50),
  });

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  };

  const parseCSV = (text) => {
    const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row = {};
      headers.forEach((h, idx) => { row[h] = values[idx]?.trim() || ""; });
      mergeListExportColumns(row);
      row._row = i + 1;
      data.push(row);
    }
    return data;
  };

  const validate = (data) => {
    const errs = [];
    const warns = [];
    data.forEach((row) => {
      if (!row.advertiser_name) errs.push({ row: row._row, msg: "Missing advertiser_name" });
      if (!row.da) errs.push({ row: row._row, msg: "Missing da (data analyst)" });
      if (row.da && !ANALYSTS.includes(row.da)) errs.push({ row: row._row, msg: `Unknown analyst: ${row.da}` });
      if (row.ae && !TEAM_MEMBERS.includes(row.ae)) errs.push({ row: row._row, msg: `Unknown AE: ${row.ae}` });
      if (row.am && !TEAM_MEMBERS.includes(row.am)) errs.push({ row: row._row, msg: `Unknown AM: ${row.am}` });
      if (row.ae && row.am && row.ae === row.am) errs.push({ row: row._row, msg: "AE and AM are the same person" });
      if (row.agency && !AGENCIES.includes(row.agency)) errs.push({ row: row._row, msg: `Unknown agency: ${row.agency}` });

      const officeRaw = String(row.office || row.chamber || "").trim();
      if (officeRaw) {
        const norm = normalizeChamber(officeRaw);
        if (norm === "Other" && !RT_CHAMBER_VALUES.includes(officeRaw)) {
          warns.push({
            row: row._row,
            msg: `office "${officeRaw}" maps to chamber Other (detail remains in race/district columns)`,
          });
        }
      }
    });
    return { errors: errs, warnings: warns };
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFileName(file.name || "");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const data = parseCSV(ev.target.result);
      const { errors: errs, warnings: warns } = validate(data);
      setRows(data);
      setErrors(errs);
      setWarnings(warns);
      setImportFatalError(null);
      setStep("preview");
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImportFatalError(null);
    setStep("importing");
    setImportProgress(null);

    const errorRows = new Set(errors.map((e) => e.row));
    const validRows = rows.filter((r) => !errorRows.has(r._row));

    try {
      const advertiserFirstRow = new Map();
      for (const row of validRows) {
        if (!advertiserFirstRow.has(row.advertiser_name)) advertiserFirstRow.set(row.advertiser_name, row);
      }
      const advertiserNamesOrder = [...advertiserFirstRow.keys()];
      const advertiserPayloads = advertiserNamesOrder.map((name) =>
        buildAdvertiserPayload(advertiserFirstRow.get(name)),
      );

      setImportProgress({ phase: "advertisers", inserted: 0, total: advertiserPayloads.length });

      const insertedAds = await base44.entities.Advertiser.createMany(advertiserPayloads, {
        chunkSize: IMPORT_CHUNK_SIZE,
        onChunk: ({ insertedSoFar, totalRows }) => {
          setImportProgress({ phase: "advertisers", inserted: insertedSoFar, total: totalRows });
        },
      });

      const advertiserIdByName = new Map(insertedAds.map((a) => [a.advertiser_name, a.id]));

      const raceOrder = [];
      const raceFirstRow = new Map();
      for (const row of validRows) {
        const chamber = normalizeChamber(row.office || row.chamber || "");
        if (!row.state || !chamber) continue;
        const gk = raceDedupKeyFromRow(row);
        if (raceFirstRow.has(gk)) continue;
        raceFirstRow.set(gk, row);
        raceOrder.push(gk);
      }

      const racePayloads = raceOrder.map((gk) => {
        const row = raceFirstRow.get(gk);
        const aid = advertiserIdByName.get(row.advertiser_name);
        if (!aid) throw new Error(`Missing advertiser id for "${row.advertiser_name}"`);
        return buildRacePayload(row, aid);
      });

      setImportProgress({ phase: "races", inserted: 0, total: racePayloads.length });

      const insertedRaces = await base44.entities.Race.createMany(racePayloads, {
        chunkSize: IMPORT_CHUNK_SIZE,
        onChunk: ({ insertedSoFar, totalRows }) => {
          setImportProgress({ phase: "races", inserted: insertedSoFar, total: totalRows });
        },
      });

      const raceIdByGk = new Map();
      raceOrder.forEach((gk, i) => {
        raceIdByGk.set(gk, insertedRaces[i].id);
      });

      const opponentPayloads = [];
      for (const row of validRows) {
        const chamber = normalizeChamber(row.office || row.chamber || "");
        if (!row.state || !chamber || !row.opponent_name) continue;
        const gk = raceDedupKeyFromRow(row);
        const raceId = raceIdByGk.get(gk);
        if (!raceId) continue;

        const oppNames = row.opponent_name.split(",").map((s) => s.trim()).filter(Boolean);
        const oppParties = (row.opponent_party || "").split(",").map((s) => s.trim());
        const bpLinks = (row.ballotpedia_url || "").split(",").map((s) => s.trim());

        for (let i = 0; i < oppNames.length; i++) {
          opponentPayloads.push({
            race_id: raceId,
            opponent_name: oppNames[i],
            party: oppParties[i] || "Other",
            ballotpedia_link: bpLinks[i] || "",
          });
        }
      }

      setImportProgress({ phase: "opponents", inserted: 0, total: opponentPayloads.length });

      await base44.entities.Opponent.createMany(opponentPayloads, {
        chunkSize: IMPORT_CHUNK_SIZE,
        onChunk: ({ insertedSoFar, totalRows }) => {
          setImportProgress({ phase: "opponents", inserted: insertedSoFar, total: totalRows });
        },
      });

      const created = validRows.length;

      setImportProgress(null);
      setImportResult({ created, skipped: errorRows.size, total: rows.length });

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error: logErr } = await supabase.from("rt_csv_import_logs").insert({
          user_id: user.id,
          user_email: user.email || "",
          file_name: importFileName || "import.csv",
          rows_total: rows.length,
          rows_valid: created,
          rows_skipped: errorRows.size,
        });
        if (logErr) console.error("rt_csv_import_logs:", logErr);
        else queryClient.invalidateQueries({ queryKey: ["rtCsvImportLogs"] });
      }

      queryClient.invalidateQueries({ queryKey: ["advertisers"] });
      queryClient.invalidateQueries({ queryKey: ["races"] });
      queryClient.invalidateQueries({ queryKey: ["opponents"] });
      setStep("done");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setImportFatalError(msg);
      setImportProgress(null);
      setStep("preview");
    }
  };

  const downloadErrors = () => {
    const errorRowNums = new Set(errors.map((e) => e.row));
    const errorData = rows.filter((r) => errorRowNums.has(r._row));
    const headers = Object.keys(errorData[0] || {}).filter((k) => k !== "_row");
    const csvLines = [headers.join(",")];
    errorData.forEach((r) => csvLines.push(headers.map((h) => r[h] || "").join(",")));
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "import_errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStep("upload");
    setRows([]);
    setErrors([]);
    setWarnings([]);
    setImportResult(null);
    setImportProgress(null);
    setImportFatalError(null);
    setImportFileName("");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Import Data</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Upload a CSV to bulk import advertisers, races, and opponents
        </p>
      </div>

      <Card className="p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">CSV import history</p>
        {importLogsLoading ? (
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </p>
        ) : importLogs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No imports recorded yet.</p>
        ) : (
          <div className="rounded-md border border-border overflow-x-auto max-h-[280px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-[10px] whitespace-nowrap">Date & time</TableHead>
                  <TableHead className="text-[10px] whitespace-nowrap">User</TableHead>
                  <TableHead className="text-[10px] whitespace-nowrap">File</TableHead>
                  <TableHead className="text-[10px] whitespace-nowrap">Rows</TableHead>
                  <TableHead className="text-[10px] whitespace-nowrap">Skipped</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-[11px] whitespace-nowrap tabular-nums">
                      {log.created_at
                        ? format(new Date(log.created_at), "MMM d, yyyy h:mm:ss a")
                        : "—"}
                    </TableCell>
                    <TableCell className="text-[11px] max-w-[180px] truncate" title={log.user_email || ""}>
                      {log.user_email || "—"}
                    </TableCell>
                    <TableCell className="text-[11px] max-w-[200px] truncate" title={log.file_name || ""}>
                      {log.file_name || "—"}
                    </TableCell>
                    <TableCell className="text-[11px] tabular-nums">{log.rows_valid ?? "—"} / {log.rows_total ?? "—"}</TableCell>
                    <TableCell className="text-[11px] tabular-nums">{log.rows_skipped ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {step === "upload" && (
        <Card className="p-8 flex flex-col items-center gap-4 border-dashed border-2">
          <FileSpreadsheet className="w-10 h-10 text-muted-foreground/50" />
          <div className="text-center space-y-2">
            <p className="text-sm font-medium">Drop your CSV here or click to browse</p>
            <p className="text-xs text-muted-foreground">
              Required: <code className="bg-muted px-1 rounded">advertiser_name</code>,{" "}
              <code className="bg-muted px-1 rounded">da</code> (List export: <code className="bg-muted px-1 rounded">data_analyst</code> is accepted)
            </p>
            <p className="text-xs text-muted-foreground">
              Optional: status, agency, state, office, race, candidate_name, ae, am, smg_plus,
              primary_date, runoff_date, markets, race_imp_rating, ge_rating, link,
              last_digital_sweep, last_linear_sweep, opponent_name, opponent_party, ballotpedia_url, notes.
              Re-importing a List View CSV also maps account_executive/am, general_election_rating, opponents, etc.
            </p>
          </div>
          <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => fileRef.current?.click()}>
            <Upload className="w-3.5 h-3.5" /> Choose File
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
        </Card>
      )}

      {step === "preview" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-xs">{rows.length} rows</Badge>
            {errors.length > 0 && (
              <Badge variant="destructive" className="text-xs gap-1">
                <AlertTriangle className="w-3 h-3" /> {errors.length} warning{errors.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>

          {importFatalError && (
            <Card className="p-4 border-destructive/40 bg-destructive/5">
              <p className="text-xs font-medium text-destructive mb-1">Import failed</p>
              <p className="text-[11px] text-destructive/90 break-words">{importFatalError}</p>
            </Card>
          )}

          {warnings.length > 0 && (
            <Card className="p-4 border-amber-200/60 bg-amber-50/50">
              <p className="text-xs font-medium text-amber-900/80 mb-2">Chamber mapping (non-blocking):</p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {warnings.slice(0, 15).map((w, i) => (
                  <p key={i} className="text-[11px] text-amber-800/80">Row {w.row}: {w.msg}</p>
                ))}
                {warnings.length > 15 && (
                  <p className="text-[11px] text-amber-700/70">...and {warnings.length - 15} more</p>
                )}
              </div>
            </Card>
          )}

          {errors.length > 0 && (
            <Card className="p-4 border-destructive/30 bg-destructive/5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-destructive">Issues found:</p>
                <Button variant="ghost" size="sm" className="text-xs gap-1 h-6" onClick={downloadErrors}>
                  <Download className="w-3 h-3" /> Download error rows
                </Button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {errors.slice(0, 20).map((e, i) => (
                  <p key={i} className="text-[11px] text-destructive/80">Row {e.row}: {e.msg}</p>
                ))}
                {errors.length > 20 && (
                  <p className="text-[11px] text-destructive/60">...and {errors.length - 20} more</p>
                )}
              </div>
            </Card>
          )}

          <div className="rounded-lg border overflow-x-auto max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-[10px]">#</TableHead>
                  <TableHead className="text-[10px]">Advertiser</TableHead>
                  <TableHead className="text-[10px]">Agency</TableHead>
                  <TableHead className="text-[10px]">DA</TableHead>
                  <TableHead className="text-[10px]">State</TableHead>
                  <TableHead className="text-[10px]">Office</TableHead>
                  <TableHead className="text-[10px]">Race</TableHead>
                  <TableHead className="text-[10px]">Opponent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.slice(0, 100).map((r) => {
                  const hasError = errors.some((e) => e.row === r._row);
                  return (
                    <TableRow key={r._row} className={hasError ? "bg-destructive/5" : ""}>
                      <TableCell className="text-[11px] text-muted-foreground">{r._row}</TableCell>
                      <TableCell className="text-[11px] font-medium">{r.advertiser_name}</TableCell>
                      <TableCell className="text-[11px]">{r.agency}</TableCell>
                      <TableCell className="text-[11px]">{r.da}</TableCell>
                      <TableCell className="text-[11px]">{r.state}</TableCell>
                      <TableCell className="text-[11px]">{r.office || r.chamber}</TableCell>
                      <TableCell className="text-[11px]">{r.race || r.district}</TableCell>
                      <TableCell className="text-[11px]">{r.opponent_name || r.opponents}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={reset}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleImport}>
              <CheckCircle2 className="w-3 h-3" /> Import{" "}
              {rows.filter((r) => !errors.some((e) => e.row === r._row)).length} valid rows
            </Button>
          </div>
        </div>
      )}

      {step === "importing" && (
        <Card className="p-12 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-sm font-medium">Importing data...</p>
          {importProgress && (
            <p className="text-xs text-muted-foreground text-center tabular-nums">
              {importProgress.phase === "advertisers" && "Advertisers"}
              {importProgress.phase === "races" && "Races"}
              {importProgress.phase === "opponents" && "Opponents"}
              {": "}
              {importProgress.inserted} / {importProgress.total} rows
            </p>
          )}
          <p className="text-xs text-muted-foreground text-center max-w-sm">
            Using batched database inserts for faster imports
          </p>
        </Card>
      )}

      {step === "done" && importResult && (
        <Card className="p-8 flex flex-col items-center gap-4">
          <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          <div className="text-center">
            <p className="text-sm font-semibold">Import Complete</p>
            <p className="text-xs text-muted-foreground mt-1">
              {importResult.created} rows processed · {importResult.skipped} skipped
            </p>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={reset}>Import Another File</Button>
        </Card>
      )}
    </div>
  );
}